/**
 * The item contract every item file fills in.
 *
 * Types only. An item is a file under `items/<kind>/`, the registry collects them, and everything
 * about what one is lives in that file. The schema is versioned from the first commit, because a
 * save holds item ids and stack counts and nothing else: the def they name is code, so a field
 * added here is a migration whether or not anybody wrote one.
 *
 * A def carries what it does rather than what it is, which is why nothing outside this file
 * switches on `kind`. Gear that can be worn or held fills in `equip`, and `aggregateLoadout` reads
 * that one field across every slot. Kind data is what the item is for beyond being equipped: a
 * weapon names its gun, ammo names its damage type, junk names what it breaks down into.
 */

import type { ArmorProfile } from '@/sim/combat/armorMitigation';
import type { DamageModifier } from '@/sim/combat/damageResolution';
import type { DamageTypeId } from '@/sim/combat/damageTypeContract';
import type { StatBlock } from '@/sim/combat/statContract';
import type { StatusEffectId } from '@/sim/combat/statusEffectContract';
import type { SlotId } from '@/sim/inventory/slotContract';
import type { WeaponId } from '@/sim/weapons/weaponContract';

/**
 * The item schema's version, bumped whenever a field here changes meaning or disappears. Saves
 * store this beside the stacks, and `migrateInventory` refuses anything it does not know.
 */
export const ITEM_SCHEMA_VERSION = 2;

/**
 * Every item in the game. The union stays explicit rather than derived from the registry, so a
 * half-registered item fails to compile instead of turning a save into a hole.
 */
export type ItemId =
  | 'glock17'
  | 'm1911'
  | 'mp5'
  | 'uzi'
  | 'm4a1'
  | 'ak47'
  | 'remington870'
  | 'benelliM4'
  | 'remington700'
  | 'm82'
  | 'm249'
  | 'combatHelmet'
  | 'plateCarrier'
  | 'scavengedGreaves'
  | 'radiationDosimeter'
  | 'medicPouch'
  | 'luckyBottleCap'
  | 'fieldBandage'
  | 'stimShot'
  | 'ballisticRounds'
  | 'shotgunShells'
  | 'scrapMetal'
  | 'ductTapeRoll'
  | 'salvagedGunParts'
  | 'vacuumTubes'
  | 'reactorPaste'
  | 'reclamationScrip';

/** The six kinds of thing an item can be. Taxonomy, and the directory an item file sits in. */
export type ItemKind = 'weapon' | 'armor' | 'consumable' | 'ammo' | 'junk' | 'currency';

export const ITEM_KIND_ORDER: readonly ItemKind[] = [
  'weapon',
  'armor',
  'consumable',
  'ammo',
  'junk',
  'currency',
];

/** How rare a drop is, worst to best. A closed set every loot table names, so not a registry. */
export type Rarity = 'common' | 'uncommon' | 'rare' | 'exotic';

export const RARITY_ORDER: readonly Rarity[] = ['common', 'uncommon', 'rare', 'exotic'];

/**
 * What an item does while it is equipped, and the only thing `aggregateLoadout` reads.
 *
 * `slots` is where the item may go, in preference order, so equipping without naming a slot takes
 * the first free one. Everything else sums across the loadout: stat deltas add onto the character's
 * block, armour thresholds and ratings add up, and modifiers land at stage 3 of the damage
 * pipeline, tagged with the item that produced them.
 */
export interface EquipEffect {
  /** Non-empty by type: gear that fits nowhere fails to compile rather than equipping nowhere. */
  readonly slots: readonly [SlotId, ...SlotId[]];
  readonly statDeltas: Partial<StatBlock>;
  readonly armor: ArmorProfile;
  readonly damageModifiers: readonly DamageModifier[];
}

interface ItemBase {
  readonly id: ItemId;
  readonly kind: ItemKind;
  readonly label: string;
  /** In-world copy, dry and short. See src/text/AGENTS.md. */
  readonly description: string;
  readonly rarity: Rarity;
  /** What one unit weighs. A stack of eight weighs eight times this. */
  readonly weightKg: number;
  /** Units one stack holds. 1 means non-stackable, and a stack of it carries an instance id. */
  readonly stackLimit: number;
  /** Null for anything that is carried rather than worn or held. */
  readonly equip: EquipEffect | null;
}

/** A gun. The stats live in `sim/weapons`, so this names the gun rather than restating it. */
export interface WeaponItem extends ItemBase {
  readonly kind: 'weapon';
  readonly weaponId: WeaponId;
}

/** Anything worn, plates and pouches alike. What it stops is in `equip.armor`. */
export interface ArmorItem extends ItemBase {
  readonly kind: 'armor';
}

/** Something used up. The use itself belongs to whoever owns health, not to the item. */
export interface ConsumableItem extends ItemBase {
  readonly kind: 'consumable';
  readonly healAmount: number;
  readonly clears: readonly StatusEffectId[];
  /** How long using it takes, in ticks. 60 is a second. */
  readonly useTicks: number;
}

/** Rounds. `roundsPerUnit` is what one unit of the stack puts in a magazine. */
export interface AmmoItem extends ItemBase {
  readonly kind: 'ammo';
  readonly damageTypeId: DamageTypeId;
  readonly roundsPerUnit: number;
}

/** Loot with no use but a value. The crafting system does not exist yet, this is its seam. */
export interface JunkItem extends ItemBase {
  readonly kind: 'junk';
  readonly scrapValue: number;
}

/**
 * Money. Spent rather than broken down, which is the whole reason it is not junk: a vendor needs
 * what one unit is worth without pretending the note is scrap somebody has not melted yet.
 */
export interface CurrencyItem extends ItemBase {
  readonly kind: 'currency';
  readonly unitValue: number;
}

export type ItemDef = WeaponItem | ArmorItem | ConsumableItem | AmmoItem | JunkItem | CurrencyItem;

/** Whether a def stacks at all. One place asks it, so nobody compares to 1 by hand twice. */
export function isStackable(def: ItemDef): boolean {
  return def.stackLimit > 1;
}
